import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLogo from '../components/AppLogo';
import { catalogAPI, appServicesAPI, clustersAPI } from '../services/api';

// A release only records its chart ("script:redis", "bitnami/postgresql"), so the logo has
// to be recovered from that string rather than an app key — same lookup Catalog.js uses.
function logoKeyFor(r) {
  const hay = `${r.chart || ''} ${r.name || ''}`.toLowerCase();
  for (const k of ['postgres', 'redis', 'mongo', 'elastic', 'rabbit', 'minio', 'kibana']) {
    if (hay.includes(k)) {
      return { postgres: 'postgresql', mongo: 'mongodb', elastic: 'elasticsearch',
               rabbit: 'rabbitmq' }[k] || k;
    }
  }
  return r.chart || 'app';
}

// A ready-to-paste URI — same scheme-inference table as Catalog.js.
function connString(c) {
  const scheme = { 5432: 'postgresql', 6379: 'redis', 27017: 'mongodb',
                   9200: 'http', 5672: 'amqp', 9000: 'http', 5601: 'http' }[Number(c.port)] || 'tcp';
  const auth = c.username ? `${c.username}:${c.password || ''}@` : '';
  return `${scheme}://${auth}${c.host}:${c.port}`;
}

const TONE = {
  running:  ['rgba(16,185,129,0.15)', 'var(--color-success-500)'],
  deployed: ['rgba(16,185,129,0.15)', 'var(--color-success-500)'],
  failed:   ['rgba(220,38,38,0.14)', 'var(--color-danger-500)'],
};
function toneFor(status) {
  return TONE[String(status || '').toLowerCase()] || ['rgba(217,119,6,0.16)', 'var(--color-warning-500)'];
}

export default function CatalogReleaseDetailPage() {
  const { releaseId } = useParams(); // app_service_id
  const navigate = useNavigate();

  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [clusters, setClusters] = useState([]);

  const [conn, setConn] = useState(null);
  const [connLoading, setConnLoading] = useState(false);

  const [resources, setResources] = useState(null);
  const [resourcesLoading, setResourcesLoading] = useState(false);

  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [tail, setTail] = useState(100);
  const [previous, setPrevious] = useState(false);
  const logsRef = useRef();

  const [ksAccessKey, setKsAccessKey] = useState('');
  const [ksSecretKey, setKsSecretKey] = useState('');
  const [ksSaving, setKsSaving] = useState(false);
  const [ksMsg, setKsMsg] = useState('');

  const [copied, setCopied] = useState('');
  const copy = (text, tag) => {
    navigator.clipboard?.writeText(text)
      .then(() => { setCopied(tag); setTimeout(() => setCopied(''), 1400); })
      .catch(() => {});
  };

  // The release list already carries everything list_releases returns (status, chart,
  // topology, bootstrap progress/failure detail) keyed by app_service_id — no new endpoint
  // needed, just find this one row. Requires knowing which cluster to query; clusters.list()
  // + AppService's own cluster_id (fetched via app-services/:id) resolves that without the
  // caller having to pass cluster_id through the URL.
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [clRes, svcRes] = await Promise.all([clustersAPI.list(), appServicesAPI.get(releaseId, false)]);
      setClusters(clRes.data || []);
      const clusterId = svcRes.data.cluster_id;
      const relRes = await catalogAPI.releases(clusterId);
      const row = (relRes.data.releases || []).find(r => String(r.app_service_id) === String(releaseId));
      if (!row) { setError('Release not found — it may have been uninstalled.'); return; }
      setRelease({ ...row, cluster_id: clusterId, image: svcRes.data.image });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load release');
    } finally {
      setLoading(false);
    }
  }, [releaseId]);

  useEffect(() => { load(); }, [load]);

  // Poll while bootstrapping, same 8s cadence Catalog.js uses, so progress advances live.
  useEffect(() => {
    if (!release || release.status === 'running' || release.status === 'failed') return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [release, load]);

  const loadConnection = useCallback(async () => {
    if (!release) return;
    setConnLoading(true);
    try {
      const r = await catalogAPI.connection(release.name, release.cluster_id);
      setConn(r.data);
    } catch (e) {
      setConn(null);
    } finally {
      setConnLoading(false);
    }
  }, [release]);

  const loadResources = useCallback(async () => {
    setResourcesLoading(true);
    try {
      const r = await appServicesAPI.getResources(releaseId);
      setResources(r.data);
    } catch (e) {
      setResources(null);
    } finally {
      setResourcesLoading(false);
    }
  }, [releaseId]);

  const fetchLogs = useCallback(async (t = tail, prev = previous) => {
    setLogsLoading(true); setLogsError(''); setLogs('');
    try {
      const r = await appServicesAPI.getLogs(releaseId, null, t, prev);
      setLogs(r.data.logs || '');
      if (r.data.error) setLogsError(r.data.error);
    } catch (e) {
      setLogsError(e.response?.data?.detail || 'Failed to fetch logs');
    } finally { setLogsLoading(false); }
  }, [releaseId, tail, previous]);

  useEffect(() => { if (tab === 'overview' && release) loadConnection(); }, [tab, release, loadConnection]);
  useEffect(() => { if (tab === 'monitoring') loadResources(); }, [tab, loadResources]);
  useEffect(() => { if (tab === 'logs') fetchLogs(tail, previous); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight; }, [logs]);

  const handleUninstall = async () => {
    if (!release) return;
    if (!window.confirm(`Uninstall "${release.name}"? This deletes its pods and data.`)) return;
    try {
      await catalogAPI.uninstall(release.name, release.cluster_id);
      navigate('/catalog');
    } catch (e) {
      alert(e.response?.data?.detail || 'Uninstall failed');
    }
  };

  const [resettingAuth, setResettingAuth] = useState(false);
  const handleResetKibanaAuth = async () => {
    if (!release) return;
    if (!window.confirm('Reset Kibana\'s auth token? This immediately invalidates the current session for anyone using Kibana.')) return;
    setResettingAuth(true);
    try {
      await catalogAPI.resetKibanaAuth(release.name, release.cluster_id);
      alert('Kibana auth reset — it restarted with a fresh token.');
      loadConnection();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to reset Kibana auth');
    } finally {
      setResettingAuth(false);
    }
  };

  const handleSaveKeystore = async () => {
    if (!ksAccessKey.trim() || !ksSecretKey.trim()) {
      setKsMsg('Both access key and secret key are required.');
      return;
    }
    setKsSaving(true); setKsMsg('');
    try {
      const r = await catalogAPI.setKeystore(release.name, release.cluster_id, ksAccessKey, ksSecretKey);
      setKsMsg(`Saved — restarted nodes: ${(r.data.updated_nodes || []).join(', ')}`);
      setKsAccessKey(''); setKsSecretKey('');
    } catch (e) {
      setKsMsg(e.response?.data?.detail || 'Failed to update keystore');
    } finally {
      setKsSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--muted-2)' }}>Loading release…</div>;
  if (error) return <div style={{ padding: 40, color: 'var(--color-danger-500)' }}>{error}</div>;
  if (!release) return null;

  const clusterName = clusters.find(c => c.id === release.cluster_id)?.name || `cluster-${release.cluster_id}`;
  const tone = toneFor(release.status);
  const st = String(release.status || '').toLowerCase();
  const failing = st === 'failed' && (release.failure_detail || release.failure_stage);
  const bootstrapping = release.phase === 'bootstrapping' && release.step_label;
  const isElastic = release.image === 'script:elasticsearch';

  const TABS = [
    { id: 'overview',   label: '⊞ Overview' },
    { id: 'monitoring', label: '📊 Monitoring' },
    { id: 'logs',       label: '📋 Logs' },
    ...(isElastic ? [{ id: 'keystore', label: '🔑 Keystore' }] : []),
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: 'var(--muted)' }}>
        <span style={{ cursor: 'pointer', color: 'var(--brand)' }} onClick={() => navigate('/catalog')}>
          App Catalog
        </span>
        <span>›</span>
        <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{release.name}</span>
      </div>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <AppLogo appKey={logoKeyFor(release)} size={48} />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>{release.name}</h1>
              <span className="badge">{release.topology || release.chart}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--muted)' }}>
              <span>{clusterName}</span>
              <span>·</span>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{release.namespace}</span>
              <span>·</span>
              {/* Same underlying AppService row as this page — jump to the raw-workload
                  view (pods/kind/image) for this namespace, cross-linking the two surfaces
                  instead of leaving them looking like separate, unrelated deployments. */}
              <span style={{ cursor: 'pointer', color: 'var(--brand)', fontSize: 12 }}
                onClick={() => navigate(`/compute/workloads?ns=${encodeURIComponent(release.namespace)}`)}>
                View in My Workloads ↗
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-block', padding: '3px 11px', borderRadius: 20,
            fontSize: 12, fontWeight: 700, background: tone[0], color: tone[1] }}>
            {bootstrapping
              ? `${release.status} — ${release.step_label} (${release.step}/${release.steps_total})`
              : `${release.status}${st === 'pending' ? ' …' : ''}`}
          </span>
          <button onClick={load} className="btn btn-secondary btn-sm">⟳ Refresh</button>
          {isElastic && release.has_linked_kibana && (
            <button onClick={handleResetKibanaAuth} disabled={resettingAuth} className="btn btn-secondary btn-sm">
              {resettingAuth ? 'Resetting…' : '🔑 Reset Kibana Auth'}
            </button>
          )}
          <button onClick={handleUninstall} className="btn btn-tinted-danger btn-sm">🗑 Uninstall</button>
        </div>
      </div>

      {/* Steps already completed, while bootstrapping */}
      {bootstrapping && !failing && (release.steps_done || []).length > 0 && (
        <div style={{ padding: '7px 11px', borderRadius: 8, fontSize: 11.5, marginBottom: 16,
          background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.3)', color: 'var(--muted)' }}>
          ✓ {release.steps_done.join(' → ')}
        </div>
      )}

      {/* Failure banner — same condition/copy as WorkloadDetailPage.js for consistency */}
      {failing && (
        <div className="banner danger" style={{ marginBottom: 16 }}>
          <span>
            {release.failure_stage === 'bootstrap-failed' ? 'Cluster init failed'
              : release.failure_stage === 'failed' ? 'Manifests failed to apply' : 'Failed'}
            : {release.failure_detail || '(no detail recorded)'}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-underline" style={{ marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={tab === t.id ? 'active' : undefined}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 16 }}>Configuration</div>
            {[
              ['Chart', release.chart],
              ['Topology', release.topology],
              ['Namespace', release.namespace],
              ['Cluster', clusterName],
              ['Status', release.db_status || release.status],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
                <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--ink)', fontFamily: label === 'Chart' ? 'monospace' : 'inherit',
                  textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{value ?? '—'}</span>
              </div>
            ))}
          </div>

          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 16 }}>Connection</div>
            {connLoading ? (
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading…</div>
            ) : conn ? (
              <>
                {[['Host', conn.host], ['Port', conn.port], ['Username', conn.username], ['Password', conn.password]]
                  .map(([label, raw]) => {
                    const value = raw == null ? '—' : typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
                    return (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
                        <span style={{ width: 84, fontSize: 12, color: 'var(--muted)', fontWeight: 600, flexShrink: 0 }}>{label}</span>
                        <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: 'var(--ink)' }}>{value}</span>
                        <button onClick={() => copy(value, label)} className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}>
                          {copied === label ? '✓' : 'Copy'}
                        </button>
                      </div>
                    );
                  })}
                {conn.host && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                      color: 'var(--muted)', marginBottom: 6 }}>Connection string</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <code style={{ flex: 1, padding: '9px 11px', borderRadius: 8, fontSize: 11.5,
                        background: 'var(--ink)', color: 'var(--border)', wordBreak: 'break-all', lineHeight: 1.5 }}>
                        {connString(conn)}
                      </code>
                      <button onClick={() => copy(connString(conn), 'uri')} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                        {copied === 'uri' ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
                {conn.url && (
                  <div style={{ marginTop: 12 }}>
                    <a href={conn.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm"
                      style={{ display: 'inline-flex', textDecoration: 'none' }}>
                      Open in browser ↗
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Not available yet — still deploying.</div>
            )}
          </div>
        </div>
      )}

      {/* ── MONITORING TAB ── */}
      {tab === 'monitoring' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Live Status
            </div>
            <button onClick={loadResources} disabled={resourcesLoading} className="btn btn-secondary btn-sm">
              {resourcesLoading ? '...' : '⟳ Refresh'}
            </button>
          </div>
          {!resources && !resourcesLoading && (
            <div style={{ color: 'var(--muted-2)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
              Click Refresh to load live status
            </div>
          )}
          {resources?.pods?.length > 0 && (
            <div className="table-card">
              <table className="ds">
                <thead>
                  <tr>{['Pod', 'Phase', 'Ready', 'Restarts'].map(h => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {resources.pods.map((pod, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', maxWidth: 220, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={pod.name}>{pod.name}</td>
                      <td>{pod.phase}{pod.reason ? ` (${pod.reason})` : ''}</td>
                      <td style={{ color: pod.ready ? 'var(--color-success-700)' : 'var(--color-danger-500)', fontWeight: 600 }}>
                        {pod.ready ? 'Yes' : 'No'}
                      </td>
                      <td style={{ color: (pod.restarts || 0) > 0 ? 'var(--color-warning-700)' : 'var(--muted-2)' }}>
                        {pod.restarts || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── LOGS TAB ── */}
      {tab === 'logs' && (
        <div style={{ background: '#0d1117', borderRadius: 10, overflow: 'hidden', height: 'calc(100vh - 280px)' }}>
          <div style={{ background: '#161b22', padding: '10px 16px', borderBottom: '1px solid #30363d',
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <select value={tail} onChange={e => { setTail(Number(e.target.value)); fetchLogs(Number(e.target.value), previous); }}
              style={{ background: '#21262d', color: '#e6edf3', border: '1px solid #30363d', borderRadius: 5, padding: '4px 8px', fontSize: 12 }}>
              {[50, 100, 200, 500, 1000].map(n => <option key={n} value={n}>Last {n}</option>)}
            </select>
            <button onClick={() => { const next = !previous; setPrevious(next); fetchLogs(tail, next); }}
              style={{ padding: '4px 12px', fontSize: 12, borderRadius: 5, cursor: 'pointer', fontWeight: 600,
                background: previous ? 'var(--ink-2)' : '#21262d',
                color: previous ? 'var(--color-warning-500)' : '#e6edf3',
                border: previous ? '1px solid var(--color-warning-500)' : '1px solid #30363d' }}>
              ⚡ Previous container
            </button>
            <button onClick={() => fetchLogs(tail, previous)}
              style={{ padding: '4px 12px', fontSize: 12, background: '#21262d', color: '#e6edf3', border: '1px solid #30363d', borderRadius: 5, cursor: 'pointer' }}>
              ↺ Reload
            </button>
            <div style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'monospace',
              color: logsLoading ? 'var(--brand)' : logsError ? 'var(--color-danger-500)' : 'var(--color-success-500)' }}>
              {logsLoading ? '⟳ Loading...' : logsError ? `✗ ${logsError}` : logs ? `✓ ${logs.split('\n').length} lines` : ''}
            </div>
          </div>
          <div ref={logsRef} style={{ flex: 1, overflowY: 'auto', height: 'calc(100% - 45px)',
            padding: '8px 0', fontFamily: "'Courier New', monospace", fontSize: 12.5, lineHeight: 1.6 }}>
            {logsLoading && <div style={{ color: '#484f58', textAlign: 'center', padding: '40px 20px' }}>Fetching logs...</div>}
            {!logsLoading && logsError && !logs && (
              <div style={{ color: 'var(--color-danger-500)', padding: '20px', fontFamily: 'monospace', fontSize: 12 }}>{logsError}</div>
            )}
            {!logsLoading && !logs && !logsError && (
              <div style={{ color: '#484f58', textAlign: 'center', padding: '40px 20px', fontSize: 13 }}>No logs available.</div>
            )}
            {!logsLoading && logs && logs.split('\n').map((line, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '0 12px' }}>
                <span style={{ color: '#3d444d', minWidth: 38, textAlign: 'right', userSelect: 'none', flexShrink: 0, fontSize: 11 }}>{i + 1}</span>
                <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  color: /error|Error|ERROR/.test(line) ? 'var(--color-danger-500)'
                       : /warn|Warn|WARN/.test(line) ? 'var(--color-warning-500)' : 'var(--border)' }}>{line}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── KEYSTORE TAB ── */}
      {tab === 'keystore' && (
        <div className="card" style={{ padding: 24, maxWidth: 480 }}>
          <h3 style={{ marginTop: 0 }}>S3 snapshot repository credentials</h3>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
            Stored in each node's Elasticsearch keystore (<code>s3.client.default.access_key</code> /
            <code> secret_key</code>), not in a Kubernetes Secret. Saving restarts each node one at a
            time to reload the keystore — the cluster stays up throughout.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13 }}>
              Access key
              <input className="input" type="password" value={ksAccessKey}
                onChange={e => setKsAccessKey(e.target.value)} disabled={ksSaving}
                style={{ display: 'block', width: '100%', marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 13 }}>
              Secret key
              <input className="input" type="password" value={ksSecretKey}
                onChange={e => setKsSecretKey(e.target.value)} disabled={ksSaving}
                style={{ display: 'block', width: '100%', marginTop: 4 }} />
            </label>
            <button className="btn btn-primary" onClick={handleSaveKeystore} disabled={ksSaving} style={{ alignSelf: 'flex-start' }}>
              {ksSaving ? 'Saving…' : 'Save & restart nodes'}
            </button>
            {ksMsg && (
              <span style={{ fontSize: 12, color: ksMsg.toLowerCase().includes('fail') ? 'var(--color-danger-500)' : 'var(--color-success-700)' }}>
                {ksMsg}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
